import type { McpToolResult } from "./hostBridge";
import { isPlainObject } from "./payload";

export interface McpAppResource {
  text: string;
  meta: unknown;
}

// One fetch per view per page load; failures are evicted so a later render can retry.
const resources = new Map<string, Promise<McpAppResource>>();

export function resetMcpAppResourceCache() {
  resources.clear();
}

async function postJson(path: string, body: unknown): Promise<unknown> {
  const response = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok)
    throw new Error(`MCP App request failed: ${response.status}`);
  return response.json();
}

export function loadMcpAppResource(uri: string): Promise<McpAppResource> {
  const cached = resources.get(uri);
  if (cached) return cached;
  const pending = postJson("/api/planner/apps/resources/read", { uri }).then(
    (data) => {
      if (!isPlainObject(data) || typeof data.text !== "string")
        throw new Error("MCP App resource is malformed");
      return { text: data.text, meta: data.meta };
    }
  );
  resources.set(uri, pending);
  pending.catch(() => {
    if (resources.get(uri) === pending) resources.delete(uri);
  });
  return pending;
}

export async function callMcpAppTool(
  name: string,
  args: Record<string, unknown>
): Promise<McpToolResult> {
  const data = await postJson("/api/planner/apps/tools/call", {
    name,
    arguments: args,
  });
  if (!isPlainObject(data)) throw new Error("MCP App tool result is malformed");
  return {
    content: Array.isArray(data.content) ? data.content : [],
    structuredContent: isPlainObject(data.structured_content)
      ? data.structured_content
      : null,
    isError: data.is_error === true,
  };
}
